import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import {
  figureDelta,
  figureValue,
  formatInteger,
  formatPercent,
  formatSignedInteger,
  lines,
} from '@components/charts/membership/revenue/figures'
import { ResponseMembershipPeriodAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Col, Row, Typography } from 'antd'

const { Text } = Typography

const DELTA_LABEL = 'vs last period'

interface Props {
  data?: ResponseMembershipPeriodAnalyticsStatistics
  isLoading: boolean
  error?: string
}

/** The four "Over the period" tiles (Figma 993:2103). */
const PeriodTiles = ({ data, isLoading, error }: Props) => {
  const money = (cents?: number | null) =>
    analyticsCurrency(cents, false, data?.currency)

  const renewalsShare = figureValue(data?.renewalsShare)
  const collectedNew = figureValue(data?.collectedNew)
  const collectedRenewals = figureValue(data?.collectedRenewals)
  const voluntaryShare = figureValue(data?.voluntaryShare)
  const voluntary = figureValue(data?.voluntaryDepartures)
  const involuntary = figureValue(data?.involuntaryDepartures)
  const netGrowthRate = figureValue(data?.netGrowthRate)

  return (
    <>
      <Row gutter={[24, 24]} align="stretch">
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Revenue collected"
            tooltip="Completed payments over the period, after coupons, split between first payments (with joining fees) and renewals."
            value={data && money(figureValue(data.collected))}
            secondary={lines(
              renewalsShare !== null &&
                `${formatPercent(renewalsShare, 0)} from renewals`,
              collectedNew !== null &&
                collectedRenewals !== null &&
                `${money(collectedNew)} new · ${money(collectedRenewals)} renewals`
            )}
            delta={{
              value: figureDelta(data?.collected),
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="New members"
            tooltip="Subscriptions that became active during the period."
            value={formatInteger(figureValue(data?.newMembers))}
            delta={{
              value: figureDelta(data?.newMembers),
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Departures"
            tooltip="Subscriptions that lost access during the period: cancelled by the member or renewal payment failed."
            value={formatInteger(figureValue(data?.departures))}
            secondary={lines(
              voluntaryShare !== null &&
                `${formatPercent(voluntaryShare, 0)} voluntary`,
              voluntary !== null &&
                involuntary !== null &&
                `${formatInteger(voluntary)} cancelled · ${formatInteger(involuntary)} payment failed`
            )}
            delta={{
              value: figureDelta(data?.departures),
              label: DELTA_LABEL,
              // More departures is bad: red up arrow.
              higherIsBetter: false,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Net growth"
            tooltip="New members minus departures over the period."
            value={formatSignedInteger(figureValue(data?.netGrowth))}
            secondary={
              netGrowthRate !== null
                ? `${netGrowthRate < 0 ? 'Shrank' : 'Grew'} ${formatPercent(Math.abs(netGrowthRate), 0)} over the period`
                : undefined
            }
            delta={{
              value: figureDelta(data?.netGrowth, 'absolute'),
              unit: 'absolute',
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      {error && <Text type="danger">{error}</Text>}
    </>
  )
}

export default PeriodTiles
